import React from "react";
import ReactPlayer from "react-player";

// Normalize URLs so missing protocol still work
const ensureHttps = (raw) => {
  if (!raw) return "";
  const trimmed = raw.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const VideoEmbed = ({ url }) => {
  const normalizedUrl = ensureHttps(url);
  if (!normalizedUrl) return null;

  // TikTok embed (best experience via iframe)
  if (normalizedUrl.includes("tiktok.com")) {
    const videoMatch = normalizedUrl.match(/\/video\/(\d+)/);
    if (videoMatch) {
      const videoId = videoMatch[1];
      return (
        <div
          className="flex flex-col items-center w-full"
          style={{ gap: "8px" }}
        >
          <div className="relative w-full" style={{ paddingBottom: "177.78%" }}>
            <iframe
              src={`https://www.tiktok.com/embed/v2/${videoId}`}
              className="absolute top-0 left-0 w-full h-full"
              style={{ border: "none", maxHeight: "738px" }}
              allowFullScreen
              scrolling="no"
              allow="encrypted-media;"
              title="TikTok video"
            ></iframe>
          </div>
          <p className="text-xs text-gray-500 text-center px-2">
            If TikTok blocks the embed, open it directly:
            <a
              href={normalizedUrl}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 underline ml-1"
            >
              View on TikTok
            </a>
          </p>
        </div>
      );
    }
  }

  // Instagram embed (posts/reels)
  if (normalizedUrl.includes("instagram.com")) {
    let embedUrl = normalizedUrl.replace(/\/$/, "");
    if (!embedUrl.includes("/embed")) {
      embedUrl = embedUrl.includes("?")
        ? embedUrl.replace("?", "/embed/?")
        : `${embedUrl}/embed/`;
    }

    return (
      <div className="flex flex-col items-center w-full" style={{ gap: "8px" }}>
        <div className="relative w-full" style={{ paddingBottom: "125%" }}>
          <iframe
            src={embedUrl}
            className="absolute top-0 left-0 w-full h-full"
            style={{ border: "none", maxHeight: "800px" }}
            allowFullScreen
            scrolling="no"
            allow="encrypted-media;"
            title="Instagram post"
          ></iframe>
        </div>
        <p className="text-xs text-gray-500 text-center px-2">
          If Instagram blocks the embed, open it directly:
          <a
            href={normalizedUrl}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 underline ml-1"
          >
            View on Instagram
          </a>
        </p>
      </div>
    );
  }

  // Facebook embed (reels / watch / videos)
  if (
    normalizedUrl.includes("facebook.com") &&
    (normalizedUrl.includes("/reel/") ||
      normalizedUrl.includes("/watch/") ||
      normalizedUrl.includes("/videos/"))
  ) {
    const encodedUrl = encodeURIComponent(normalizedUrl);
    return (
      <div className="flex flex-col items-center w-full" style={{ gap: "8px" }}>
        <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
          <iframe
            src={`https://www.facebook.com/plugins/video.php?href=${encodedUrl}&show_text=false&width=560&height=315`}
            className="absolute top-0 left-0 w-full h-full"
            style={{ border: "none" }}
            allowFullScreen
            scrolling="no"
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
            title="Facebook video"
          ></iframe>
        </div>
        <p className="text-xs text-gray-500 text-center px-2">
          If the video is blocked from embedding (Facebook privacy or
          copyright), open it directly:
          <a
            href={normalizedUrl}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 underline ml-1"
          >
            View on Facebook
          </a>
        </p>
      </div>
    );
  }

  // Default: Use ReactPlayer for YouTube, Vimeo, etc.
  return <ReactPlayer url={normalizedUrl} controls width="100%" />;
};

export default VideoEmbed;
